import { createTheme, type PaletteMode } from '@mui/material/styles';
import { brand, brandDark, typography } from './tokens';
import { getPaletteOptions } from './palette';
import { getTypographyOptions } from './typography';
import { getComponentOverrides } from './components';

// Factory function to create theme for a given mode
export function getTheme(mode: PaletteMode = 'light') {
  const isDark = mode === 'dark';

  // Mode-aware text colours
  const headingColor = isDark ? brand.paper : brand.ink;
  const bodyColor = isDark ? brand.paper : brand.ink;
  const secondaryText = isDark ? brandDark.text2 : brand.muted;

  const palette = getPaletteOptions(mode, isDark, bodyColor, secondaryText);
  const typographyOpts = getTypographyOptions(headingColor, bodyColor, secondaryText);
  const components = getComponentOverrides(isDark);

  return createTheme({
    palette,
    typography: typographyOpts,
    components,
    zIndex: {
      snackbar: 2000,
      tooltip: 2100,
    },
  });
}

// Default light theme (backward compat)
export const theme = getTheme('light');

// Export brand tokens for use in custom components.
export { brand, brandDark, typography };

// Custom theme extensions for Trade Connector specific use cases
export const tradeConnectorTheme = {
  ...theme,
  custom: {
    // Portal-specific color mappings (business = ink anchor, trade pro = emerald)
    portal: {
      client: {
        primary: brand.ink,
        accent: brand.accent,
        background: brand.paper,
      },
      trade: {
        primary: brand.accent,
        accent: brand.accentDeep,
        background: brand.paper,
      },
    },
    // Status colors for job management
    status: {
      live: brand.accent,
      pending: brand.warning,
      completed: brand.accent,
      cancelled: brand.error,
      onHold: brand.warning,
    },
    // Typography for specific use cases
    typography: {
      dataViz: {
        fontFamily: typography.bodyFont,
        fontWeight: 600,
        fontSize: '0.75rem',
        lineHeight: 1.4,
        letterSpacing: '0.5px',
      },
      cta: {
        fontFamily: typography.bodyFont,
        fontWeight: 500,
        fontSize: '1rem',
        letterSpacing: '0.5px',
      },
    },
  },
};
